import { format, useMask } from '@react-input/mask';
import { padWithMask, stripMaskTail, toMaskPattern } from '@/utils/mask-utils';
import React, { ChangeEvent, InputHTMLAttributes, ReactElement, Ref, forwardRef, useCallback, useMemo } from 'react';
import type { Replacement, Track } from '@react-input/mask';

export interface MaskInputProps extends InputHTMLAttributes<HTMLInputElement> {
  mask: string;
  maskPlaceholder: string;
  alwaysShowMask?: boolean;
  track?: Track;
}

/**
 * Re-presents a change event carrying a different value, keeping both prototype chains intact.
 * A spread cannot do this: a DOM element's properties and a React event's methods both live on their prototypes,
 * so `{...event.target}` yields an object with no element properties at all and `{...event}` drops `preventDefault`.
 * Layering with `Object.create` keeps a consumer that reads `target.type` or calls `event.preventDefault()` working,
 * instead of leaving it to survive on the two keys re-added here.
 *
 * @param event The change event as the mask engine produced it.
 * @param value The value the consumer should see in place of the rendered one.
 * @param name The field name, for a wrapper that reports the field under a name of its own.
 */
export const withMaskedValue = (
  event: ChangeEvent<HTMLInputElement>,
  value: string,
  name?: string,
): ChangeEvent<HTMLInputElement> => {
  const overrides: PropertyDescriptorMap = { value: { value, enumerable: true } };
  if (name != null) {
    overrides.name = { value: name, enumerable: true };
  }
  return Object.create(event, {
    target: { value: Object.create(event.target, overrides), enumerable: true },
  });
};

/**
 * Masked input component delegating to `@react-input/mask`.
 * Designed to be used as `inputComponent` for MUI `OutlinedInput`.
 * Mask format character is `9` (digit); every other character is a literal that the user can neither edit nor delete.
 *
 * The `value` prop holds the characters that fill the mask slots, not a fully masked string.
 * Values already carrying the mask literals are accepted as long as those literals are not digits —
 * otherwise a literal digit would be consumed as slot data.
 * A mask with a digit literal, such as the `'+7'` phone prefix,
 * therefore needs its consumer to strip the prefix before passing the value down (see `RussianPhoneNumberField`).
 */
export const MaskInput = forwardRef<HTMLInputElement, MaskInputProps>(function MaskInput(
  { mask, maskPlaceholder, alwaysShowMask, track, value, onChange, ...rest }: MaskInputProps,
  ref: Ref<HTMLInputElement>,
): ReactElement {
  const maskPattern: string = useMemo(() => toMaskPattern(mask, maskPlaceholder), [mask, maskPlaceholder]);
  const replacement: Replacement = useMemo(() => ({ [maskPlaceholder]: /\d/ }), [maskPlaceholder]);

  const maskRef = useMask({ mask: maskPattern, replacement, showMask: alwaysShowMask, track });

  const setRef = useCallback(
    (element: HTMLInputElement | null): void => {
      // An `autoFocus` field is focused before the ref callback runs, yet the library arms its chain
      // at registration through an active-element check, so the detach guard below catches that case too.
      //
      // `@react-input/core` stops its caret-poll chain only on a `blur` event, and clearing the ref
      // unregisters the library listener while leaving the chain armed: a field unmounted while holding
      // focus would keep rescheduling forever, on the page and past the test environment alike.
      // The chain is armed exactly while the field holds the focus, and React detaches the ref before
      // removing the node, so a field that is still the active element here may carry a live chain —
      // a field that lost the focus earlier stopped it with that very `blur` and needs no artificial event,
      // which also keeps the consumer's `onBlur` from firing on a field it has already seen blurred.
      if (element == null && maskRef.current != null && document.activeElement === maskRef.current) {
        maskRef.current.dispatchEvent(new Event('blur'));
      }
      maskRef.current = element as HTMLInputElement;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [maskRef, ref],
  );

  const formatted: string = format(value != null ? String(value) : '', { mask: maskPattern, replacement });
  const displayValue: string = alwaysShowMask ? padWithMask(formatted, maskPattern) : formatted;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (!onChange) {
      return;
    }
    if (!alwaysShowMask) {
      onChange(event);
      return;
    }
    const masked: string = stripMaskTail(event.target.value, maskPattern);
    onChange(withMaskedValue(event, masked));
  };

  return <input ref={setRef} value={displayValue} onChange={handleChange} {...rest} />;
});
